import { Alert } from '@factorialco/deprecated-design-system'
import {
  ExpensesEntitiesExpensableStatusEnum,
  ExpensesInsightsDocument,
  ExpensesInsightsQuery,
} from 'generated/resources/graphql'

import useFilters from 'modules/core/components/DataTable/hooks/useFilters'
import { Employee, useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { buildExpensesApprovedWidget } from 'modules/expenses/components/my_expenses/widgets/ExpensesApprovedWidget'
import { buildReimbursementsApprovedWidget } from 'modules/expenses/components/my_expenses/widgets/ReimbursementsApprovedWidget'
import { buildReimbursementsPendingApprovalWidget } from 'modules/expenses/components/my_expenses/widgets/ReimbursementsPendingApprovalWidget'
import { PageDateFilters } from 'modules/expenses/lib/PageDateFiltersContext'
import { datesToLocalString } from 'modules/expenses/lib/common'
import { DataTableWidgetInsights } from 'modules/finance/components/Widgets/common/widgets'

type FilterVariable = Record<string, Record<string | number | symbol, unknown>>
interface ExpensesInsightsProps {
  groupId?: number
  employee?: Employee
  dateFilters?: PageDateFilters
}

const ExpensesStatuses: Record<
  'approvedExpenses' | 'pendingExpenses',
  ExpensesEntitiesExpensableStatusEnum[]
> = {
  approvedExpenses: ['approved', 'sent_to_pay', 'paid', 'in_payroll'],
  pendingExpenses: ['pending', 'changes_requested'],
}

const extractFilteredCurrencies = (
  dataTableFilters: Record<string | number | symbol, unknown>,
  employee?: Employee
) => {
  const forcedCurrency =
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    (dataTableFilters.currency as { in: string[] })?.['in'][0] ||
    employee?.legalEntity?.currency ||
    'EUR'
  const forcedReimbursableCurrency =
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    (dataTableFilters.reimbursableCurrency as { in: string[] })?.['in'][0] || forcedCurrency
  return {
    forcedCurrency,
    forcedReimbursableCurrency,
  }
}
const hasMultipleCurrencies = (data: ExpensesInsightsQuery | undefined) =>
  (data?.expenses?.expensablesConnection?.aggregate?.terms?.reimbursableCurrency?.values?.length ||
    0) > 1 ||
  (data?.expenses?.expensablesConnection?.aggregate?.terms?.currency?.values?.length || 0) > 1

const buildInsightsParams = (
  getFiltersVariables: () => FilterVariable,
  locale: string,
  groupId: number | undefined,
  employee: Employee | undefined,
  dateFilters?: PageDateFilters
) => {
  const dataTableFilters = getFiltersVariables().filter ?? {}
  const { id: employeeId } = employee || {}

  const amountFormatter = (value: unknown, currency: string) =>
    formattedAmount({ amountCents: Number(value), currency, locale })
  const { forcedCurrency, forcedReimbursableCurrency } = extractFilteredCurrencies(
    dataTableFilters,
    employee
  )
  const { from, to } = datesToLocalString(dateFilters?.startDate, dateFilters?.endDate)

  return {
    amountFormatter,
    forcedCurrency,
    forcedReimbursableCurrency,
    commonWidgetParams: {
      queryArgs: {
        groupIds: groupId ? [Number(groupId)] : undefined,
        employeeIds: employeeId ? [Number(employeeId)] : undefined,
        from,
        to,
      },
      filters: dataTableFilters,
    },
  }
}

export const MyExpensesInsights = ({ groupId, employee, dateFilters }: ExpensesInsightsProps) => {
  const { getFiltersVariables } = useFilters()
  const {
    access: { locale },
  } = useCurrentSession()

  const { commonWidgetParams, amountFormatter, forcedCurrency, forcedReimbursableCurrency } =
    buildInsightsParams(getFiltersVariables, locale, groupId, employee, dateFilters)
  const { data } = useQuery(ExpensesInsightsDocument, {
    variables: {
      ...commonWidgetParams.queryArgs,
      filter: commonWidgetParams.filters,
    },
  })

  if (hasMultipleCurrencies(data)) {
    return <Alert color="note">{i18n.t('expenses_v2.list.insights.empty_case.description')}</Alert>
  }

  return (
    <DataTableWidgetInsights
      useBox
      widgetWidth="full"
      widgets={[
        buildExpensesApprovedWidget({
          ...commonWidgetParams,
          widgetTitle: i18n.t('expenses_v2.list.insights.labels.expenses_approved'),
          queryArgs: {
            ...commonWidgetParams.queryArgs,
            statuses: ExpensesStatuses.approvedExpenses,
          },
          prepareDataArgs: { amountFormatter, forcedCurrency: forcedCurrency },
        }),
        buildReimbursementsPendingApprovalWidget({
          ...commonWidgetParams,
          widgetTitle: i18n.t('expenses_v2.list.insights.labels.reimbursements_pending_approval'),
          queryArgs: {
            ...commonWidgetParams.queryArgs,
            statuses: ExpensesStatuses.pendingExpenses,
          },
          prepareDataArgs: { amountFormatter, forcedCurrency: forcedReimbursableCurrency },
        }),
        buildReimbursementsApprovedWidget({
          ...commonWidgetParams,
          widgetTitle: i18n.t('expenses_v2.list.insights.labels.reimbursements_approved'),
          queryArgs: {
            ...commonWidgetParams.queryArgs,
            statuses: ExpensesStatuses.approvedExpenses,
          },
          prepareDataArgs: { amountFormatter, forcedCurrency: forcedReimbursableCurrency },
        }),
      ]}
    />
  )
}
